export type UnknownRecord = Record<string, unknown>

export type ContentfulLink = {
  sys: { type: 'Link'; linkType: 'Asset' | 'Entry'; id: string }
}

export const isRecord = (value: unknown): value is UnknownRecord =>
  typeof value === 'object' && value !== null && !Array.isArray(value)

export const isContentfulLink = (value: unknown, linkType: 'Asset' | 'Entry'): value is ContentfulLink =>
  isRecord(value) &&
  isRecord(value.sys) &&
  value.sys.type === 'Link' &&
  value.sys.linkType === linkType &&
  typeof value.sys.id === 'string'

/**
 * A `ContentfulAsset` node from the page query.
 *
 * `__typename` is checked first and on purpose: under CONTENTFUL_PREVIEW a draft asset can have a
 * null `gatsbyImageData` (see `gatsbyHelpers/previewAssetResolvers.js`) and a null `file.url`, and
 * recognising it only by those would mean the one asset most in need of an overlay is the one we
 * refuse to overlay — the editor could never swap a broken image for a working one. The value
 * checks stay as a fallback for queries that do not select `__typename`.
 */
export const isGatsbyAsset = (value: unknown): value is UnknownRecord =>
  isRecord(value) &&
  (value.__typename === 'ContentfulAsset' ||
    isRecord(value.gatsbyImageData) ||
    (isRecord(value.file) && typeof value.file.url === 'string'))

export const isGatsbyEntryRef = (value: unknown): value is UnknownRecord =>
  isRecord(value) && typeof value.contentful_id === 'string' && !isGatsbyAsset(value)

export const isLongTextField = (value: unknown): value is { text: string } =>
  isRecord(value) && typeof value.text === 'string'

export const isRichTextField = (value: unknown): value is { raw: string } =>
  isRecord(value) && typeof value.raw === 'string'

export const isRichTextDocument = (value: unknown): boolean => isRecord(value) && value.nodeType === 'document'

// Some queries format Date fields at build time via `formatString` (e.g. SnackBar's `updatedAt(formatString: "X")`),
// so the build time value can differ in shape from the raw timestamp Contentful holds. Overlaying one on the other
// would swap a formatted value for a raw one. Blog dates are queried raw and formatted in the component.
const ISO_DATE = /^\d{4}-\d{2}-\d{2}([T ]|$)/

export const isOverlayableText = (value: unknown): value is string => typeof value === 'string' && !ISO_DATE.test(value)

export type ContentfulAssetFile = {
  url: string
  contentType?: string
  fileName?: string
  details?: { image?: { width?: number; height?: number } }
}

export const isContentfulAssetFile = (value: unknown): value is ContentfulAssetFile =>
  isRecord(value) && typeof value.url === 'string' && value.url.length > 0

/**
 * Live preview sends an asset three ways: as an unresolved Asset Link, as a resolved CPA asset
 * (`{ sys: { type: 'Asset' }, fields }`), and sometimes as bare fields (`{ title, file }`) with no
 * `sys` at all. Only the last two carry the file inline.
 */
export const getInlineAssetFields = (value: unknown): UnknownRecord | undefined => {
  if (!isRecord(value)) return undefined
  if (isRecord(value.fields) && isContentfulAssetFile(value.fields.file)) return value.fields
  if (isContentfulAssetFile(value.file)) return value
  return undefined
}

export const getInlineAssetId = (value: unknown): string | undefined => {
  if (!isRecord(value) || !isRecord(value.sys)) return undefined
  return typeof value.sys.id === 'string' ? value.sys.id : undefined
}
